import { Component, OnInit, ViewChild } from '@angular/core';
import { ITreeOptions } from '@circlon/angular-tree-component';
import { NbDialogRef } from '@nebular/theme';
import { SearchService } from '../../../@theme/service/search.service';
import { ToastService } from '../../../@theme/service/toast.service';
import { Company } from '../../model/company';
import { PageResult } from '../../model/page-result';
import { PolicyTarget } from '../../model/policy-target';
import { User } from '../../model/user';
import { Arrays } from '../../util/arrays';
import { Columns } from '../../util/column-builder.util';
import { Strings } from '../../util/Strings';
import { PeriodColumnComponent } from '../dynamic-data-grid/renderer/period-column.component';
import { AdvancedTreeComponent } from '../tree/tree.component';

@Component({
  selector: 'ngx-policy-target-dialog',
  templateUrl: './policy-target-dialog.component.html',
  styleUrls: ['./policy-target-dialog.component.scss']
})
export class PolicyTargetDialogComponent implements OnInit {

  @ViewChild('tree') public tree: AdvancedTreeComponent;

  public overlayNoRowsTemplate = '대상을 등록후 적용해주세요.';

  public companies: any[] = [];
  public orgSearchName;
  public currentOrgSearchName;
  public applyTarget: PageResult<PolicyTarget> = new PageResult();
  public targetCount: number = 0;
  public treeOptions: ITreeOptions = {
    getChildren: this.getUserNode.bind(this),
    isExpandedField: 'expanded',
  };

  public columns = Columns.builder()
    .add('targetType', '구분').set('targetType', 'maxWidth', 120)
    .add('groupName', '그룹').set('groupName', 'minWidth', 150)
    .add('userName', '사용자').set('userName', 'minWidth', 200)
    .add('periods', '기간')
      .set('periods', 'minWidth', 260)
      .set('periods', 'cellClass', "text-center")
      .set('periods', 'cellRendererFramework', PeriodColumnComponent)
      .set('periods', 'cellRendererParams', { displayAttr: 'periods', startDate: 'startDate', endDate: 'endDate' })
  .build();

  constructor(private searchService: SearchService,
    private toast: ToastService,
    private dialogRef: NbDialogRef<PolicyTargetDialogComponent>) { }

  ngOnInit(): void {
    this.findGroups();
  }

  getUserNode(node: any): Promise<any> {
    const companyId = node.data.id;
    return new Promise<any>((resolve, reject) => {
      this.findGroupWithUser(null, companyId).then((res) => {
        resolve(res.map(user => { return this.parseUser(user) }));
      });
    });
  }

  findOrg() {
    if (this.currentOrgSearchName == this.orgSearchName)
      return;

    if (Strings.isBlank(this.orgSearchName)) {
      this.findGroups();
    } else {
      this.findGroupWithUser(this.orgSearchName).then((res) => {
        this.companies = this.parseGroupWithUser(res, true);
        this.currentOrgSearchName = this.orgSearchName;

        this.tree?.treeComponent?.updateData.subscribe(() => {
          // TODO 데이터가 변경될때 isExpandedField를 지정했지만 반영되지 않는 현상이 있어 강제로 열여주는 방식을 구현함 수정필요.
          if (!Strings.isBlank(this.orgSearchName))
            this.tree.expandAll();
        });
      });
    }
  }

  resetOrgSearchName() {
    if (Strings.isBlank(this.currentOrgSearchName)) {
      this.orgSearchName = this.currentOrgSearchName = null;
      return;
    }

    this.refreshGroupNodes();
  }

  refreshGroupNodes() {
    this.tree.collapseAll();
    this.findGroups();
  }

  onSelectedItem(selectedNode: any) {
    const node = selectedNode;

    const targets: PolicyTarget[] = Arrays.deepCopy(this.applyTarget.content || []).map(a => new PolicyTarget(a));
    if (targets.some(t => t.targetType == node.data.type && t.targetId === node.data.id)) {
      this.toast.info('이미 등록된 대상 입니다.');
      return;
    }

    targets.push(new PolicyTarget({ targetType: node.data.type
        , targetId: node.data.id
        , user: node.data.type == 'USER' ? new User({ name: node.data.name, email: node.data.email, company: new Company({name: node.parent.data.name})}) : null
        , group: node.data.type == 'GROUP' ? new Company({name: node.data.name}) : null
      }));
    this.applyTarget = new PageResult<PolicyTarget>({content: targets, totalElements: targets.length});
    this.targetCount = this.applyTarget.content.length;
  }

  getDisplayName(node: any) {
    if (node.data.type == "GROUP") {
      return node.data.name;
    } else if (node.data.type == "USER") {
      return `[${node.parent?.data.name || 'Unkown Group'}] ${node.data.name} ${node.data.email}`;
    } else {
      console.error("not supported type:", node.data.type);
      return null;
    }
  }

  findGroups(): Promise<any> {
    this.currentOrgSearchName = this.orgSearchName = null;
    return new Promise<any>((resolve, reject) => {
      this.findGroupWithUser().then((res) => {
        this.companies = res.map(group => { return this.parseGroup(group, false)});
        return resolve(this.companies);
      });
    });
  }

  parseUser(user: any) {
    return {
      id: user.id,
      name: `${user.name}${user.attrs['JOBTITLE'] ? (' (' + user.attrs['JOBTITLE'] + ')') : ''}`,
      email: user.attrs['EMAIL'],
      hasChildren: false,
      type: 'USER',
      expanded: false,
      tooltip: `${user.name}${user.attrs['JOBTITLE'] ? (' (' + user.attrs['JOBTITLE'] + ')') : ''} / ${user.attrs['EMAIL']}`
    }
  }

  parseGroup(group: any, isExpanded: boolean) {
    return {
      id: group.id,
      name: group.name,
      hasChildren: group.hasChildren,
      children: group.children,
      type: 'GROUP',
      expanded: group.hasChildren ? isExpanded : false,
      tooltip: group.name
    }
  }

  parseGroupWithUser(res: any[], expend: boolean): any[] {
    let nodes: any[] = [];
    res.forEach(dto => {
      let node: any = dto.attrs['TYPE'] == 'USER' ? this.parseUser(dto) : this.parseGroup(dto, expend);

      if (node.hasChildren) {
        node.children = this.parseGroupWithUser(node.children, expend);
      }

      nodes.push(node);
    });

    return nodes;
  }

  findGroupWithUser(keyword: string = null, groupId: string = null): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.searchService.findGroupWithUser(keyword, groupId).subscribe(
        (d: any) => {
          return resolve(d && d.result ? d.data || [] : []);
        },
        (err) => reject(err),
      );
    });
  }

  apply() {
    this.dialogRef.close(this.applyTarget?.content);
  }

  close() {
    this.dialogRef.close();
  }

  reset() {
    this.applyTarget = new PageResult<PolicyTarget>();
  }

  onClickItem(item: any): void {

    if (this.applyTarget?.totalElements == 0 || !item || !item.data) return;

    const node = item.data;
    const newContents = this.applyTarget.content.filter(t => { t.targetId != node.targetId && t.targetType != node.targetType });
    this.applyTarget = new PageResult<PolicyTarget>({content: newContents, totalElements: newContents.length})
  }
}
